/**
 * Error Types for the application
 * 
 * This file defines the different types of errors that can occur in the application.
 * Each error type has a specific severity level and handling strategy.
 */

// Error severity levels
export const ErrorSeverity = {
  INFO: 'info',       // Informational messages, not critical
  WARNING: 'warning', // Warnings that don't break functionality but should be noted
  ERROR: 'error',     // Errors that affect functionality but don't crash the app
  CRITICAL: 'critical' // Critical errors that may crash the app or compromise security
};

// Error categories to group related errors
export const ErrorCategory = {
  AUTHENTICATION: 'authentication', // Auth-related errors
  NETWORK: 'network',               // Network/API request errors
  DATABASE: 'database',             // Database operation errors
  VALIDATION: 'validation',         // Input validation errors
  SECURITY: 'security',             // Security-related errors (CSRF, XSS, etc.)
  STORAGE: 'storage',               // Local storage errors
  RENDERING: 'rendering',           // UI rendering errors
  UNKNOWN: 'unknown'                // Uncategorized errors
};

// Map Firebase error codes to our internal error categories
export const mapFirebaseErrorToCategory = (errorCode) => {
  if (!errorCode) return ErrorCategory.UNKNOWN;

  if (errorCode.startsWith('auth/')) {
    return ErrorCategory.AUTHENTICATION;
  }

  if (errorCode.startsWith('firestore/') || errorCode.startsWith('storage/')) {
    return ErrorCategory.DATABASE;
  }

  if (errorCode.includes('network') || errorCode.includes('unavailable')) {
    return ErrorCategory.NETWORK;
  }

  if (errorCode.includes('permission-denied') || errorCode.includes('unauthorized')) {
    return ErrorCategory.SECURITY;
  }

  return ErrorCategory.UNKNOWN;
};

// Map Firebase error codes to severity levels
export const mapFirebaseErrorToSeverity = (errorCode) => {
  if (!errorCode) return ErrorSeverity.ERROR;

  // Critical security errors
  if (
    errorCode.includes('csrf') ||
    errorCode.includes('security') ||
    errorCode === 'auth/account-exists-with-different-credential'
  ) {
    return ErrorSeverity.CRITICAL;
  }

  // Network errors are usually warnings
  if (errorCode.includes('network') || errorCode === 'auth/timeout') {
    return ErrorSeverity.WARNING;
  }

  // User input errors are usually info level
  if (
    errorCode === 'auth/invalid-email' ||
    errorCode === 'auth/weak-password' ||
    errorCode === 'auth/popup-closed-by-user' ||
    errorCode === 'auth/popup-blocked'
  ) {
    return ErrorSeverity.INFO;
  }

  // Default to standard error
  return ErrorSeverity.ERROR;
};
